import React from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Eye, Send, Briefcase, TrendingUp } from "lucide-react";

const STAGE_LABELS = { sourced: "Sourced", contacted: "Contacted", shortlisted: "Shortlisted", interviewing: "Interviewing", offer: "Offer", hired: "Hired", rejected: "Rejected" };
const STAGE_COLORS = { sourced: "#94a3b8", contacted: "#60a5fa", shortlisted: "#FBBF24", interviewing: "#a78bfa", offer: "#34d399", hired: "#22c55e", rejected: "#f87171" };

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="neu-card rounded-2xl p-5" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}1a` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="font-heading text-2xl font-bold text-white">{value}</p>
      <p className="text-[11px] text-slate-400 mt-1">{label}</p>
    </div>
  );
}

export default function AnalyticsPanel({ analytics }) {
  if (!analytics) return null;
  const maxFunnel = Math.max(...Object.values(analytics.pipeline_funnel), 1);

  return (
    <div data-testid="recruiter-analytics-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Eye} label="Profile Views" value={analytics.total_profile_views} color="#60a5fa" />
        <StatCard icon={Send} label="Messages Sent" value={analytics.total_messages_sent} color="#a78bfa" />
        <StatCard icon={TrendingUp} label="Response Rate" value={`${analytics.response_rate}%`} color="#34d399" />
        <StatCard icon={Briefcase} label="Active Job Posts" value={analytics.active_job_postings} color="#FBBF24" />
      </div>

      <div className="neu-card rounded-2xl p-6 mb-6" data-testid="analytics-funnel-chart">
        <h3 className="font-heading font-semibold text-white text-sm mb-4">Pipeline Funnel</h3>
        <div className="space-y-3">
          {Object.entries(analytics.pipeline_funnel).map(([stage, count]) => (
            <div key={stage} className="flex items-center gap-3">
              <span className="text-xs text-slate-400 w-24 shrink-0">{STAGE_LABELS[stage]}</span>
              <div className="flex-1 h-6 rounded-md bg-white/5 neu-inset overflow-hidden">
                <div className="h-full rounded-md transition-all" style={{ width: `${Math.max((count / maxFunnel) * 100, count > 0 ? 4 : 0)}%`, backgroundColor: STAGE_COLORS[stage] }} />
              </div>
              <span className="text-xs font-mono text-white w-6 text-right">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {analytics.views_last_14_days?.length > 0 && (
        <div className="neu-card rounded-2xl p-6" data-testid="analytics-views-chart">
          <h3 className="font-heading font-semibold text-white text-sm mb-4">Profile Views (Last 14 Days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={analytics.views_last_14_days}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#8993ab", fontSize: 10 }} />
              <YAxis tick={{ fill: "#8993ab", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#131a29", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#5b9dff" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
